/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { TabContext, TabPanel } from '@mui/lab';
import { Heading } from './Heading';
import { Tabs } from './Tabs';
import { Stack } from '@mui/material';

const Container = styled.div<{ borderRight?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  border-right: ${({ theme, borderRight }) => (!borderRight ? 0 : `1px solid ${theme.line.light}`)};

  & .MuiTabPanel-root {
    padding: 0;
    height: 100%;
  }
`;

export type BucketFile = {
  embedded: boolean;
  last_modification: string;
  name: string;
  size: string;
};

interface Props {
  // the title of the sidebar
  title: string;
  // the icon beside the title
  icon?: ReactNode;
  // the current selected tab
  tab?: string;
  // a function for setting the current tab, must be defined or tabs will not be rendered
  setTab?: React.Dispatch<string>;
  // a array of tabs to display, and the body to render for each. must be defined or tabs will not be rendered
  tabs?: { label: string; value: string; disabled?: boolean; body: ReactNode; queue?: boolean }[];
  // true if you want to add a border to the right of the sidebar
  borderRight?: boolean;
  // if you would like to add a dynamic Queue tab that shows the size of the queue, you should set this value to the queue size.
  queueSize?: number;
  // the body component to render if no tabs are defined
  body?: ReactNode;
  // if true, a close button will be rendered in the top right of the sidebar
  onClose?: () => void;
  // define this as a ReactNode to render a notice instead of tabs
  replaceTabsWithNotice?: ReactNode;
  // when true, will shift tabs 1rem from left on divider line
  insetTabs?: boolean;
  // the value to inset the tabs by
  insetValue?: string;
}

export const SideBar: React.FC<Props> = (props) => {
  const { title, insetTabs = true, insetValue } = props;

  const hasTabs = Boolean(!props?.replaceTabsWithNotice && props?.tabs?.length && props?.tab && props?.setTab);

  return (
    <Container borderRight={props?.borderRight}>
      <Heading
        title={title}
        icon={props?.icon}
        onClose={props?.onClose}
        hasTabs={hasTabs}
        padded={insetTabs}
        insetValue={insetValue}
      />
      {!props?.replaceTabsWithNotice && props?.tabs?.length && props?.tab && props?.setTab && (
        <TabContext value={props?.tab || ''}>
          <Tabs
            queueSize={props?.queueSize !== undefined ? props.queueSize : false}
            tabs={props?.tabs}
            setTab={props?.setTab}
            flush
            insetTabs={insetTabs}
            insetValue={insetValue}
          />
          {props.tabs.map((tab) => (
            <TabPanel key={tab.value} value={tab.value}>
              {tab.body}
            </TabPanel>
          ))}
        </TabContext>
      )}
      {props?.replaceTabsWithNotice && <Stack padding={2}>{props?.replaceTabsWithNotice}</Stack>}
      {props?.body && props.body}
    </Container>
  );
};
